import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { DataSheet } from '../../types/dataSheetTypes';
import dataSheetService from '../../services/dataSheetService';
import Button from '../../components/common/Button/Button';
import InputField from '../../components/common/InputField/InputField';
import Modal from '../../components/common/Modal/Modal';
import DataSheetForm from '../../components/DataSheet/DataSheetForm';
import { formatDateDMY } from '../../utils/helpers';
import styles from './DataSheetList.module.scss';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DataSheetList: React.FC = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [dataSheets, setDataSheets] = useState<DataSheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDataSheets, setFilteredDataSheets] = useState<DataSheet[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingDataSheet, setEditingDataSheet] = useState<DataSheet | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [dataSheetToDelete, setDataSheetToDelete] = useState<DataSheet | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Check if user has data sheet management permissions
  const canManageDataSheets = auth.user?.role === 'admin'

  useEffect(() => {
    loadDataSheets();
  }, []);

  const loadDataSheets = async () => {
    setLoading(true);
    try {
      const dataSheets = await dataSheetService.getDataSheets();
      setDataSheets(Array.isArray(dataSheets) ? dataSheets : []);
      setFilteredDataSheets(Array.isArray(dataSheets) ? dataSheets : []);
    } catch (error) {
      toast.error('Failed to load data sheets');
    } finally {
      setLoading(false);
    }
  };

  // Filter data sheets based on search term
  useEffect(() => {
    const filtered = dataSheets.filter(dataSheet =>
      dataSheet.sheet_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredDataSheets(filtered);
  }, [searchTerm, dataSheets]);

  const handleDelete = async (dataSheetId: string) => {
    setDataSheetToDelete(dataSheets.find(ds => ds.id === dataSheetId) || null);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!dataSheetToDelete) return;
    
    setDeleting(true);
    try {
      await dataSheetService.deleteDataSheet(dataSheetToDelete.id);
      toast.success('Data sheet deleted successfully!');
      setDataSheets(prev => prev.filter(ds => ds.id !== dataSheetToDelete.id));
      setFilteredDataSheets(prev => prev.filter(ds => ds.id !== dataSheetToDelete.id));
      setDeleteModalOpen(false);
      setDataSheetToDelete(null);
    } catch (error) {
      toast.error('Failed to delete data sheet');
      console.error('Failed to delete data sheet:', error);
    } finally {
      setDeleting(false);
    }
  };

  // const downloadCSV = () => {
  //   const headers = ['Sheet Name', 'Items Count', 'Created Date', 'Updated Date'];
  //   const csvContent = [
  //     headers.join(','),
  //     ...filteredDataSheets.map(dataSheet => [
  //       dataSheet.sheet_name,
  //       dataSheet.items?.length || 0,
  //       formatDateDMY(dataSheet.created_at),
  //       formatDateDMY(dataSheet.updated_at)
  //     ].join(','))
  //   ].join('\n');

  //   const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  //   const link = document.createElement('a');
  //   const url = URL.createObjectURL(blob);
  //   link.setAttribute('href', url);
  //   link.setAttribute('download', `data_sheets_${new Date().toLocaleDateString('en-CA')}.csv`);
  //   link.style.visibility = 'hidden';
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };

  const handleCreateDataSheet = () => {
    setIsCreateModalOpen(true);
  };

  const handleEditDataSheet = (dataSheet: DataSheet) => {
    setEditingDataSheet(dataSheet);
    setIsEditModalOpen(true);
  };

  const handleSubmitDataSheet = async (dataSheetData: any) => {
    setFormLoading(true);
    try {
      const newDataSheet = await dataSheetService.createDataSheet(dataSheetData);
      setDataSheets(prev => [newDataSheet, ...prev]);
      setFilteredDataSheets(prev => [newDataSheet, ...prev]);
      setIsCreateModalOpen(false);
      toast.success('Data sheet created successfully!');
    } catch (error) {
      toast.error('Failed to create data sheet');
      console.error('Failed to create data sheet:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditDataSheetSubmit = async (dataSheetData: any) => {
    if (!editingDataSheet) return;
    
    setFormLoading(true);
    try {
      const updatedDataSheet = await dataSheetService.updateDataSheet({
        ...dataSheetData,
        id: editingDataSheet.id
      });
      setDataSheets(prev => prev.map(ds => ds.id === editingDataSheet.id ? updatedDataSheet : ds));
      setFilteredDataSheets(prev => prev.map(ds => ds.id === editingDataSheet.id ? updatedDataSheet : ds));
      setIsEditModalOpen(false);
      setEditingDataSheet(null);
      toast.success('Data sheet updated successfully!');
    } catch (error) {
      toast.error('Failed to update data sheet');
      console.error('Failed to update data sheet:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setEditingDataSheet(null);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading data sheets...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h2>Data Sheets</h2>
          <p>Manage your data sheets and their items</p>
        </div>
        
        <div className={styles.actions}>
          <div className={styles.searchSection}>
            <InputField
              name="search"
              type="text"
              placeholder="Search data sheets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          
          <div className={styles.buttonGroup}>
            {/* <Button
              onClick={downloadCSV}
              variant="secondary"
              className={styles.exportButton}
            >
              Export CSV
            </Button> */}
            
            {canManageDataSheets && (
              <Button
                onClick={handleCreateDataSheet}
                variant="primary"
                className={styles.createButton}
              >
                Create Data Sheet
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className={styles.content}>
        {filteredDataSheets.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📋</div>
            <h3>No data sheets found</h3>
            <p>Create your first data sheet to get started</p>
            {canManageDataSheets && (
              <Button onClick={handleCreateDataSheet} variant="primary">
                Create Data Sheet
              </Button>
            )}
          </div>
        ) : (
          <div className={styles.dataSheetGrid}>
            {filteredDataSheets.map((dataSheet) => (
              <div key={dataSheet.id} className={styles.dataSheetCard}>
                <div className={styles.cardHeader}>
                  <h3 className={styles.sheetName}>{dataSheet.sheet_name}</h3>
                  <div className={styles.itemCount}>
                    {dataSheet.items?.length || 0} items
                  </div>
                </div>
                
                <div className={styles.cardContent}>
                  <div className={styles.metaInfo}>
                    <span>Created: {formatDateDMY(dataSheet.created_at)}</span>
                    <span>Updated: {formatDateDMY(dataSheet.updated_at)}</span>
                  </div>
                  
                  {/* {dataSheet.items && dataSheet.items.length > 0 && (
                    <div className={styles.previewItems}>
                      <h4>Preview Items:</h4>
                      <ul>
                        {dataSheet.items.slice(0, 3).map((item) => (
                          <li key={item.id}>
                            <strong>{item.item_des}:</strong> {item.value}
                          </li>
                        ))}
                        {dataSheet.items.length > 3 && (
                          <li className={styles.moreItems}>
                            +{dataSheet.items.length - 3} more items
                          </li>
                        )}
                      </ul>
                    </div>
                  )} */}
                </div>
                
                <div className={styles.cardActions}>
                  <Button
                    onClick={() => navigate(`/data-sheets/${dataSheet.id}`)}
                    variant="secondary"
                    size="sm"
                  >
                    View Details
                  </Button>
                  
                 
                
                      <Button
                        onClick={() => handleEditDataSheet(dataSheet)}
                        variant="secondary"
                        size="sm"
                      >
                        Edit
                      </Button>
                      {canManageDataSheets && (
                      <Button
                        onClick={() => handleDelete(dataSheet.id)}
                        variant="danger"
                        size="sm"
                      >
                        Delete
                      </Button>
                    
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        title="Create Data Sheet"
        size="xl"
      >
        <DataSheetForm
          onSubmit={handleSubmitDataSheet}
          onCancel={handleCloseModal}
          loading={formLoading}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        title="Edit Data Sheet"
        size="xl"
      >
        <DataSheetForm
          dataSheet={editingDataSheet}
          onSubmit={handleEditDataSheetSubmit}
          onCancel={handleCloseModal}
          loading={formLoading}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirm Delete"
        size="xl"
      >
        <div className={styles.deleteModal}>
          <p>Are you sure you want to delete "{dataSheetToDelete?.sheet_name}"?</p>
          <p>This action cannot be undone and will delete all associated items.</p>
          <div className={styles.deleteActions}>
            <Button
              onClick={() => setDeleteModalOpen(false)}
              variant="secondary"
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              variant="danger"
              loading={deleting}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      <ToastContainer />
    </div>
  );
};

export default DataSheetList; 